import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useTaskAssignees } from '@/hooks/tasks/use-task-assignees';

interface TaskCardAssigneesProps {
  taskId: string;
  hideWhenEmpty?: boolean;
  maxVisible?: number;
}

export const TaskCardAssignees: React.FC<TaskCardAssigneesProps> = ({
  taskId,
  hideWhenEmpty = false,
  maxVisible = 3
}) => {
  const { data: assignees = [], isLoading, error } = useTaskAssignees(taskId);

  // Helper function to get initials from name
  const getInitials = (firstName: string, username: string) => {
    if (firstName) return firstName.charAt(0).toUpperCase();
    if (username) return username.charAt(0).toUpperCase();
    return 'U';
  };

  // Don't render anything if there are no assignees and hideWhenEmpty is true
  if (hideWhenEmpty && (!assignees || assignees.length === 0)) {
    return null;
  }

  // Show loading skeleton
  if (isLoading) {
    return (
      <div className="flex items-center gap-1">
        <div className="flex -space-x-1">
          {[...Array(2)].map((_, i) => (
            <div 
              key={i}
              className="w-6 h-6 rounded-full bg-muted animate-pulse border-2 border-background"
            />
          ))}
        </div>
      </div>
    );
  }

  // Handle errors silently - this component is often used conditionally
  // and RLS errors are expected in some contexts
  if (error) {
    console.debug('TaskCardAssignees: Access denied or error fetching assignees:', error.message);
    return null;
  }

  // Don't render if no assignees
  if (!assignees || assignees.length === 0) {
    return null;
  }

  const visibleAssignees = assignees.slice(0, maxVisible);
  const remainingCount = Math.max(0, assignees.length - maxVisible);

  return (
    <div className="flex items-center gap-1">
      <div className="flex -space-x-1">
        {visibleAssignees.map((assignee) => (
          <Avatar 
            key={assignee.assignee_id} 
            className="w-6 h-6 border-2 border-background"
            title={assignee.display_name}
          >
            <AvatarImage 
              src={assignee.profile_picture_url} 
              alt={assignee.display_name} 
            />
            <AvatarFallback className="text-xs">
              {getInitials(assignee.first_name, assignee.username)}
            </AvatarFallback>
          </Avatar>
        ))}
      </div>
      {remainingCount > 0 && (
        <span className="text-xs text-muted-foreground font-medium">
          +{remainingCount}
        </span>
      )}
    </div>
  );
}; 